/**
 * directoryService.ts
 *
 * Read-only employee directory for the magic-link dispatcher and the
 * Launch EPR employee picker.
 *
 * Primary source: /contacts collection — Account Portal's canonical person
 * registry, kept in sync with Microsoft 365 (source 'm365-sync') plus
 * manual/CSV entries. Most employees exist ONLY here (contact-only, no
 * Firebase Auth account), so reading /users would miss them.
 *
 * Fallback: legacy /users collection — used only when /contacts is empty
 * (e.g. emulator runs or an environment that predates the contacts
 * migration), so the picker never goes blank.
 *
 * Returns the rich DirectoryEmployee shape including directManagerId +
 * level needed to auto-suggest a reviewer panel.
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { DirectoryEmployee } from '../types/magicLink';
import type { EmployeeLevel } from '../types/optics2026';

interface RawContactDoc {
  displayName?: string;
  email?: string;
  jobTitle?: string;
  unitId?: string;
  unitName?: string;
  directManagerId?: string;
  level?: string;
  grade?: string;
  isLeader?: boolean;
  status?: string;
  user_type?: string;
  isExternal?: boolean;
  mergedIntoContactId?: string;
  // Legacy field names — docs written by OKR Tracker's m365Sync
  // generation (pre-Account-Portal schema) carry `title` + `department`
  // instead of `jobTitle` + `unitName`. Prod still holds ~270 of them.
  title?: string;
  department?: string;
}

interface RawUserDoc {
  name?: string;
  displayName?: string;
  email?: string;
  jobTitle?: string;
  unitId?: string;
  directManagerId?: string;
  level?: string;
  grade?: string;
  isActive?: boolean;
  isDeleted?: boolean;
}

const VALID_LEVELS: readonly EmployeeLevel[] = ['general', 'supervisor', 'dept_head'];

function deriveLevel(raw: { level?: string; grade?: string; isLeader?: boolean }): EmployeeLevel {
  if (raw.level && (VALID_LEVELS as readonly string[]).includes(raw.level)) {
    return raw.level as EmployeeLevel;
  }
  // Fallback: derive from grade if present (G1–G3 → general, G4–G5 → supervisor, G6+ → dept_head)
  const g = raw.grade?.toUpperCase().replace(/\D/g, '');
  if (g) {
    const n = parseInt(g, 10);
    if (!Number.isNaN(n)) {
      if (n >= 6) return 'dept_head';
      if (n >= 4) return 'supervisor';
    }
  }
  // Contacts carry an isLeader flag (people-manager) but often no grade.
  // HR can still override per employee via "Adjust level" on the form.
  if (raw.isLeader) return 'supervisor';
  return 'general';
}

/** Fetch the active employee directory with unit names resolved. */
export async function fetchDirectory(): Promise<DirectoryEmployee[]> {
  const fromContacts = await fetchFromContacts();
  if (fromContacts.length > 0) return fromContacts;
  return fetchFromLegacyUsers();
}

/**
 * /contacts — canonical M365-synced registry.
 *
 * No orderBy in the query: equality-filter + orderBy would demand a
 * composite index in the shared project; we sort client-side instead.
 */
async function fetchFromContacts(): Promise<DirectoryEmployee[]> {
  const snap = await getDocs(
    query(collection(db, 'contacts'), where('status', '==', 'active')),
  );

  return snap.docs
    .map(d => ({ id: d.id, raw: d.data() as RawContactDoc }))
    // Employees only — vendors/guests stay out of the review directory.
    // Missing user_type means a pre-classification doc; keep it (same
    // default as Account Portal's getActiveEmployeeRoster) UNLESS it is
    // also flagged isExternal — legacy vendor contacts auto-created by
    // OKR cascade have isExternal:true and no user_type. Classified
    // employees pass regardless of isExternal (field staff may use a
    // non-Rascal email domain).
    .filter(({ raw }) =>
      raw.mergedIntoContactId === undefined
      && (raw.user_type === 'employee'
          || (!raw.user_type && raw.isExternal !== true)),
    )
    .map(({ id, raw }) => ({
      id,
      name: String(raw.displayName ?? ''),
      email: String(raw.email ?? '').toLowerCase().trim(),
      jobTitle: raw.jobTitle ?? raw.title,
      unitId: raw.unitId,
      unitName: raw.unitName ?? raw.department,
      directManagerId: raw.directManagerId,
      level: deriveLevel(raw),
      isActive: true,
    }))
    .filter(e => e.email && e.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Legacy /users — only consulted when /contacts is empty. */
async function fetchFromLegacyUsers(): Promise<DirectoryEmployee[]> {
  const [usersSnap, unitsSnap] = await Promise.all([
    getDocs(query(collection(db, 'users'), where('isActive', '!=', false))),
    getDocs(collection(db, 'units')),
  ]);

  const unitsMap: Record<string, string> = {};
  unitsSnap.docs.forEach(d => { unitsMap[d.id] = String(d.data().name ?? d.id); });

  return usersSnap.docs
    .filter(d => d.data().isDeleted !== true)
    .map(d => {
      const raw = d.data() as RawUserDoc;
      return {
        id: d.id,
        name: String(raw.name ?? raw.displayName ?? ''),
        email: String(raw.email ?? '').toLowerCase().trim(),
        jobTitle: raw.jobTitle,
        unitId: raw.unitId,
        unitName: raw.unitId ? unitsMap[raw.unitId] : undefined,
        directManagerId: raw.directManagerId,
        level: deriveLevel(raw),
        isActive: raw.isActive !== false,
      };
    })
    .filter(e => e.email && e.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Auto-suggest reviewer panel for a given reviewee.
 *
 * Defaults:
 *   - self          → reviewee itself (always included)
 *   - line_manager  → directManagerId, if present
 *   - peer          → others with same unitId, excluding reviewee + manager + direct reports
 *   - direct_report → users whose directManagerId === reviewee.id
 *
 * Caller (HR) confirms / edits before dispatch.
 */
export interface SuggestedReviewer {
  reviewerRole: 'self' | 'line_manager' | 'peer' | 'direct_report';
  employee: DirectoryEmployee;
}

export function suggestReviewerPanel(
  reviewee: DirectoryEmployee,
  directory: DirectoryEmployee[],
  opts?: { maxPeers?: number; maxDirectReports?: number },
): SuggestedReviewer[] {
  const maxPeers = opts?.maxPeers ?? 3;
  const maxDirectReports = opts?.maxDirectReports ?? 3;

  const out: SuggestedReviewer[] = [];

  // Self — always
  out.push({ reviewerRole: 'self', employee: reviewee });

  // Line manager
  if (reviewee.directManagerId) {
    const lm = directory.find(e => e.id === reviewee.directManagerId);
    if (lm) out.push({ reviewerRole: 'line_manager', employee: lm });
  }

  // Direct reports
  const directReports = directory
    .filter(e => e.directManagerId === reviewee.id && e.id !== reviewee.id)
    .slice(0, maxDirectReports);
  directReports.forEach(e => out.push({ reviewerRole: 'direct_report', employee: e }));

  // Peers — same unit, not the reviewee, not the line manager, not a direct report
  const drIds = new Set(directReports.map(e => e.id));
  const peers = directory
    .filter(e =>
      reviewee.unitId &&
      e.unitId === reviewee.unitId &&
      e.id !== reviewee.id &&
      e.id !== reviewee.directManagerId &&
      !drIds.has(e.id)
    )
    .slice(0, maxPeers);
  peers.forEach(e => out.push({ reviewerRole: 'peer', employee: e }));

  return out;
}
